import { getIpClient } from '@/lib/commonHelpers';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom React hook to get user's IP address using getIpClient utility.
 * Fetches IP on mount and provides a function to refetch when needed.
 *
 * @returns {{ userIp: string | null, isLoading: boolean, error: string | null, refetchIp: () => Promise<void> }} - An object containing the user's IP, loading state, error state, and refetch function.
 */
export const useUserIp = () => {
  const [userIp, setUserIp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchIp = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const ip = await getIpClient();
      setUserIp(ip || null);
    } catch (err) {
      console.error('Failed to fetch user IP:', err);
      setError('Failed to fetch IP address');
      setUserIp(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refetchIp = useCallback(async () => {
    await fetchIp();
  }, [fetchIp]);

  useEffect(() => {
    fetchIp();
  }, [fetchIp]);

  return {
    userIp,
    isLoading,
    error,
    refetchIp,
  };
};
